import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FQAN_razina, Razina } from '@app/components';
import { DokumentacijaHierarhijaComponent } from '@app/components/dokumentacija/dokumentacija-hierarhija.component';
import { DokItemInfo } from '@app/components/dokumentacija/dokumentacija-types';
import { EarhivTreeNode } from '@app/components/hierarhija/types';
import { AppUser, AuthService } from '@app/components/login.component';
import { TreeViewProviderDirective } from '@app/providers';
import { ActionComponent, NotificationService } from '@ntap/core';
import { TreeNode } from 'primeng/api';
import { Observable, first, map, of, tap, withLatestFrom } from 'rxjs';
import { PremjestiDokJedinicuService } from './premjesti-dok-jedinicu.service';

@Component({
  selector: 'app-premjesti-action',
  template: `
    <ng-container *ngIf="action$ | async as action">
      <p-button
        [icon]="'pi ' + (action.icon ? 'pi-' + action.icon : 'pi-hidden')"
        [ngClass]="action.name + ' hide-label-responsive'"
        [label]="(label$ | async) ?? ''"
        ntapDebounceClick
        (onClick)="executeAction()"
        [disabled]="(isDisabled$ | async) ?? true"
        [pTooltip]="action.tooltip ?? ''"
        tooltipPosition="left"
        [badge]="clipboardItemCount > 0 ? clipboardItemCount.toString() : ''"
      ></p-button>
    </ng-container>
    <ng-container *ngIf="clipboardItemCount > 0">
      <button
        pButton
        pRipple
        type="button"
        icon="pi pi-times"
        pTooltip="Očisti"
        (click)="clearClipboard()"
        class="p-button-rounded p-button-text"
      ></button>
    </ng-container>
  `
})
export class PremjestiDokJedinicuActionComponent
  extends ActionComponent
  implements OnInit, OnDestroy
{
  private selectedItem: any;
  private selectedNode: TreeNode & EarhivTreeNode = {};
  private nodeToMove: TreeNode & EarhivTreeNode = {};
  private isClipboardEmpty = true;
  protected clipboardItemCount = 0;

  readonly LABEL_CUT = 'Premjesti';
  readonly LABEL_PASTE = 'Zalijepi';
  label$: Observable<string> = of(this.LABEL_CUT);

  constructor(
    protected listViewProvider: TreeViewProviderDirective,
    protected premjestiDokJedinicuService: PremjestiDokJedinicuService,
    @Inject(DokumentacijaHierarhijaComponent)
    private parentComponent: DokumentacijaHierarhijaComponent,
    protected notificationService: NotificationService,
    @Inject(AuthService)
    private authService: AuthService
  ) {
    super();
  }

  ngOnInit(): void {
    this.addSubscription(
      this.listViewProvider.selectedItems$
        .pipe(
          tap((selected) => {
            if (!!selected && selected.length == 1) {
              this.selectedItem = selected[0];
              this.selectedNode =
                this.listViewProvider.selectedNodes instanceof Array
                  ? this.listViewProvider.selectedNodes[0]
                  : this.listViewProvider.selectedNodes;
              if (this.isClipboardEmpty) {
                Object.assign(this.nodeToMove, this.selectedNode);
              }
            }
          })
        )
        .subscribe()
    );
    this.addSubscription(
      this.premjestiDokJedinicuService.clipboardJedinica$
        .pipe(
          tap((clipboardJedinica) => {
            this.isClipboardEmpty = !clipboardJedinica.item;
            this.clipboardItemCount = !this.isClipboardEmpty ? 1 : 0;
            this.changeActionLabel();
          })
        )
        .subscribe()
    );
  }

  protected override initDisabledObservable(): void {
    super.initDisabledObservable();
    this.isDisabled$ = this.listViewProvider.selectedItems$.pipe(
      withLatestFrom(this.authService.validUser$),
      map(([selectedItems, user]) => {
        return this.isDisabled(selectedItems, user);
      })
    );
  }

  private isDisabled(selectedItems: any[], user: AppUser): boolean {
    const singleItemSelected = selectedItems && selectedItems.length === 1;
    const item = singleItemSelected ? selectedItems[0] : null;
    const dokInfo = item ? new DokItemInfo(item) : null;

    // nadredena_jedinica_gradiva u parentu se ne popunjava dobro pa idemo preko TreeNode-a?
    return (
      !dokInfo ||
      dokInfo.locked ||
      (dokInfo.permission < 2 && !this.isZaduzenNaPredmetu(item, user)) ||
      (!this.selectedNode.parent && this.isClipboardEmpty)
    );
  }

  private isZaduzenNaPredmetu(item: any, user: AppUser): boolean {
    const razina = item[FQAN_razina];
    if (razina !== Razina.PREDMET) {
      return true;
    }
    const zaduzeniId = item['seup~predmet~zaduzen_za_predmet'] as number;
    return zaduzeniId === user.userId?.value;
  }

  override executeAction(): void {
    if (this.isClipboardEmpty) {
      this.premjestiDokJedinicuService.cutJedinica(this.selectedItem);
      this.clearSelection();
    } else {
      this.premjestiDokJedinicuService
        .pasteJedinica(this.selectedItem)
        .pipe(first())
        .subscribe({
          next: () => {
            this.notificationService.success(
              'Premještanje je uspješno izvršeno.'
            );
            this.refreshList();
          },
          error: (e) => {
            this.notificationService.error(
              'Dogodila se greška prilikom premještanja: ' + e.error.message ||
                e.message
            );
          },
          complete: () => {
            this.clearClipboard();
            this.clearSelection();
          }
        });
    }
  }

  private changeActionLabel(): void {
    this.label$ = of(this.isClipboardEmpty ? this.LABEL_CUT : this.LABEL_PASTE);
  }

  private refreshList(): void {
    // refresh destination node
    this.listViewProvider.nodeRefresh(this.selectedNode);

    // refresh parent of source node
    if (this.nodeToMove.parent) {
      this.listViewProvider.nodeRefresh(this.nodeToMove.parent);
    }
    // hack: refresh childrenListNode - distinctUntilKeyChanged('id')
    const tempNode = Object.assign({}, this.selectedNode, { id: null });
    this.parentComponent.onNodeSelect({ node: tempNode });
    this.parentComponent.onNodeSelect({ node: this.selectedNode });
  }

  private clearSelection(): void {
    this.listViewProvider.selectedNodes = [];
  }

  protected clearClipboard(): void {
    this.premjestiDokJedinicuService.clearClipboard();
    this.nodeToMove = {};
  }
}
